/**
 * Real WHO 2006 Child Growth Standards LMS reference data, birth through 24 months — `BACKLOG.md`
 * M8, `emr-po/BACKLOG.md`'s E11 row, replacing the plausible-shaped placeholder values
 * `growthCharts.ts`'s `REFERENCE_TABLE` previously carried for this age range (see
 * `cdcGrowthReferenceData.ts` for the CDC 2-20yr half, integrated separately and earlier).
 *
 * **Source**: `weianthro.dta` (weight-for-age), `lenanthro.dta` (length/height-for-age), and
 * `hcanthro.dta` (head-circumference-for-age) from
 * https://github.com/unicef-drp/igrowup_update (UNICEF's own distribution of the WHO Child Growth
 * Standards macro's reference tables — the same source `emr-po-0d` identified and the peer session
 * that sourced the CDC half pointed back to for this remaining piece), fetched directly at
 * https://raw.githubusercontent.com/unicef-drp/igrowup_update/master/{weianthro,lenanthro,hcanthro}.dta.
 * Fetched and independently verified 2026-08-24: real Stata `.dta` content readable by `pandas.
 * read_stata` (SHA-256 84093c46e3190726c3bbc2f5db28949a48b303788db81c27ba8559bb999e210e /
 * 6a63bff0f299c286e3767f3d5bf5196df074d16e29aaf474271476577d0b6e7f /
 * 1ebdfd2a757bfe39e2ef3efffafd0ea61e37515ae3e33c2520e96e4f8fcf5cbb for weight/length/head-
 * circumference respectively), the sex column (`__000001`, undocumented in the file itself)
 * confirmed empirically as `1 = male, 2 = female` by the same divergent-medians method used for the
 * CDC file (`cdcGrowthReferenceData.ts`'s own header) -- birth weight 3.35kg (sex=1) vs 3.23kg
 * (sex=2), matching the standard "boys slightly heavier at birth" direction -- and medians
 * spot-checked against commonly-cited WHO 2006 published figures (e.g. 24-month male length median
 * 87.8cm, matching this project's own prior placeholder value for that exact point almost exactly,
 * and boys'/girls' birth weight and length both landing within the published ranges).
 *
 * **Recumbent length vs. standing height, and why this file only needs length**: `lenanthro.dta`
 * carries a `loh` column ("length or height") that is genuinely `'L'` for every row through
 * `_agedays: 730` and `'H'` from `_agedays: 731` on -- WHO measures recumbent length under 24
 * months and standing height at and after, and that transition lands on *exactly* the same day
 * (731) `growthCharts.ts` already uses as its own WHO/CDC cutover boundary. This file only pulls
 * the `loh = 'L'` (length) rows, through day 730 -- the CDC-side `growthfile_cdc_ext.csv` (see
 * `cdcGrowthReferenceData.ts`) already supplies real standing-height data from day 731 on, so no
 * "height" rows from this WHO source are needed at all.
 *
 * **Sampling**: monthly (`growthCharts.ts`'s own `DAYS_PER_MONTH = 30.4375` convention, rounded the
 * same way, `Math.round(months * 30.4375)`), months 0 through 23, plus an explicit final row at
 * `ageDays: 730` -- the exact last WHO-covered day, one short of the month-24 grid point
 * (`Math.round(24 * 30.4375) = 731`), which is where `cdcGrowthReferenceData.ts`'s real data begins.
 * No gap, no overlap, at the handoff. The source file is genuinely daily-resolution (`_agedays` 0-1856,
 * covering birth through age 5 in this repository's own reference tables) -- monthly sampling here
 * is a large, defensible improvement over the old placeholder's five coarse tabulated points
 * (birth/6/12/18/24 months), without embedding hundreds of near-identical daily rows; real per-day
 * values remain available in the source `.dta` files if a future pass wants finer resolution.
 *
 * **BMI-for-age, added 2026-08-24** (`BACKLOG.md` M8's BMI-for-age follow-up): `bmianthro.dta` from
 * the same repository, fetched directly at
 * https://raw.githubusercontent.com/unicef-drp/igrowup_update/master/bmianthro.dta. Fetched and
 * independently verified: real Stata content (SHA-256
 * 25c6d11a5a208e011c5b2f5cc72a8342b21b3e88c681bfb32cd939d990e282c9), `_agedays` empirically confirmed
 * to run 0-1856 -- **WHO's BMI-for-age chart starts at birth, unlike CDC's 24-month start** -- and
 * the sex column (`__000001`) independently re-confirmed as `1 = male, 2 = female` for this specific
 * file (not just assumed from the other three) by the same divergent-medians method: male median BMI
 * exceeds female at every checked age past infancy (e.g. 12 months: 16.80 vs. 16.36; 24 months: 15.74
 * vs. 15.41), the expected direction, while the two are nearly indistinguishable at birth (13.41 vs.
 * 13.34) -- consistent with the standard pattern, not a coincidence of an arbitrary label.
 *
 * Like `lenanthro.dta`, this file carries a `loh` column that is `'L'` (recumbent-length-based BMI)
 * through `_agedays: 730` and `'H'` (standing-height-based BMI) from `_agedays: 731` on -- the same
 * boundary day used throughout this module. `WHO_BMI_REFERENCE` below pulls only the `loh = 'L'` rows,
 * through day 730, matching `WHO_LENGTH_CM_REFERENCE`'s own convention exactly: the CDC-side
 * `CDC_BMI_REFERENCE` (see `cdcGrowthReferenceData.ts`) already supplies real height-based BMI data
 * from day 731 on, so no `'H'` rows from this file are needed. Sampling matches this file's own
 * weight/length/head-circumference tables: monthly, months 0 through 23, plus the exact day 730.
 * Medians spot-checked for plausibility against commonly-cited WHO 2006 BMI-for-age figures: ~13.3-
 * 13.4 at birth, ~15.4-15.7 at 24 months (both sexes), matching this table's own `m` values exactly.
 */

interface WhoReferenceRow {
  readonly ageDays: number;
  readonly l: number;
  readonly m: number;
  readonly s: number;
}

/** Weight-for-age, birth through 730 days (24 months), monthly LMS values plus the exact 24-month boundary day. */
export const WHO_WEIGHT_KG_REFERENCE: Readonly<
  Record<'male' | 'female', readonly WhoReferenceRow[]>
> = {
  male: [
    { ageDays: 0, l: 0.3487, m: 3.3464, s: 0.14602 },
    { ageDays: 30, l: 0.2303, m: 4.4525, s: 0.13413 },
    { ageDays: 61, l: 0.1969, m: 5.5714, s: 0.12382 },
    { ageDays: 91, l: 0.174, m: 6.369, s: 0.11732 },
    { ageDays: 122, l: 0.1551, m: 7.0069, s: 0.11313 },
    { ageDays: 152, l: 0.1396, m: 7.5077, s: 0.11081 },
    { ageDays: 183, l: 0.1256, m: 7.9389, s: 0.10957 },
    { ageDays: 213, l: 0.1134, m: 8.2963, s: 0.10902 },
    { ageDays: 244, l: 0.1019, m: 8.62, s: 0.10882 },
    { ageDays: 274, l: 0.0917, m: 8.9019, s: 0.10881 },
    { ageDays: 304, l: 0.0821, m: 9.1618, s: 0.1089 },
    { ageDays: 335, l: 0.0729, m: 9.4136, s: 0.10906 },
    { ageDays: 365, l: 0.0645, m: 9.646, s: 0.10925 },
    { ageDays: 396, l: 0.0563, m: 9.8772, s: 0.10949 },
    { ageDays: 426, l: 0.0487, m: 10.0944, s: 0.10976 },
    { ageDays: 457, l: 0.0412, m: 10.3139, s: 0.11008 },
    { ageDays: 487, l: 0.0343, m: 10.5228, s: 0.11041 },
    { ageDays: 517, l: 0.0276, m: 10.7289, s: 0.11078 },
    { ageDays: 548, l: 0.021, m: 10.9393, s: 0.1112 },
    { ageDays: 578, l: 0.0149, m: 11.1409, s: 0.11163 },
    { ageDays: 609, l: 0.0087, m: 11.3478, s: 0.11212 },
    { ageDays: 639, l: 0.0029, m: 11.5474, s: 0.11261 },
    { ageDays: 670, l: -0.0029, m: 11.7528, s: 0.11315 },
    { ageDays: 700, l: -0.0083, m: 11.951, s: 0.11369 },
    { ageDays: 730, l: -0.0136, m: 12.1482, s: 0.11425 },
  ],
  female: [
    { ageDays: 0, l: 0.3809, m: 3.2322, s: 0.14171 },
    { ageDays: 30, l: 0.1727, m: 4.1716, s: 0.13738 },
    { ageDays: 61, l: 0.0959, m: 5.1315, s: 0.12998 },
    { ageDays: 91, l: 0.0407, m: 5.8393, s: 0.12622 },
    { ageDays: 122, l: -0.0053, m: 6.428, s: 0.12401 },
    { ageDays: 152, l: -0.0428, m: 6.8959, s: 0.12274 },
    { ageDays: 183, l: -0.0759, m: 7.3016, s: 0.12204 },
    { ageDays: 213, l: -0.1039, m: 7.6416, s: 0.12178 },
    { ageDays: 244, l: -0.1292, m: 7.9534, s: 0.12181 },
    { ageDays: 274, l: -0.1507, m: 8.2259, s: 0.12199 },
    { ageDays: 304, l: -0.1698, m: 8.4769, s: 0.12222 },
    { ageDays: 335, l: -0.1873, m: 8.7207, s: 0.12247 },
    { ageDays: 365, l: -0.2022, m: 8.9462, s: 0.12267 },
    { ageDays: 396, l: -0.216, m: 9.1722, s: 0.12283 },
    { ageDays: 426, l: -0.2277, m: 9.3861, s: 0.12294 },
    { ageDays: 457, l: -0.2385, m: 9.6038, s: 0.12299 },
    { ageDays: 487, l: -0.2478, m: 9.8124, s: 0.12303 },
    { ageDays: 517, l: -0.2561, m: 10.0196, s: 0.12305 },
    { ageDays: 548, l: -0.2637, m: 10.2324, s: 0.12309 },
    { ageDays: 578, l: -0.2702, m: 10.4372, s: 0.12315 },
    { ageDays: 609, l: -0.2763, m: 10.6481, s: 0.12324 },
    { ageDays: 639, l: -0.2814, m: 10.8521, s: 0.12335 },
    { ageDays: 670, l: -0.2862, m: 11.0633, s: 0.12351 },
    { ageDays: 700, l: -0.2903, m: 11.2684, s: 0.12369 },
    { ageDays: 730, l: -0.294, m: 11.4741, s: 0.12389 },
  ],
};

/** Recumbent length-for-age, birth through 730 days (24 months) -- WHO measures recumbent length under 24 months and standing height at and after, matching this codebase's own WHO/CDC cutover exactly. Monthly LMS values plus the exact 24-month boundary day. */
export const WHO_LENGTH_CM_REFERENCE: Readonly<
  Record<'male' | 'female', readonly WhoReferenceRow[]>
> = {
  male: [
    { ageDays: 0, l: 1, m: 49.8842, s: 0.03795 },
    { ageDays: 30, l: 1, m: 54.6645, s: 0.03559 },
    { ageDays: 61, l: 1, m: 58.4384, s: 0.03423 },
    { ageDays: 91, l: 1, m: 61.4013, s: 0.03329 },
    { ageDays: 122, l: 1, m: 63.9041, s: 0.03257 },
    { ageDays: 152, l: 1, m: 65.8912, s: 0.03204 },
    { ageDays: 183, l: 1, m: 67.6435, s: 0.03165 },
    { ageDays: 213, l: 1, m: 69.1615, s: 0.03139 },
    { ageDays: 244, l: 1, m: 70.6224, s: 0.03124 },
    { ageDays: 274, l: 1, m: 71.9714, s: 0.03117 },
    { ageDays: 304, l: 1, m: 73.2653, s: 0.03118 },
    { ageDays: 335, l: 1, m: 74.5464, s: 0.03125 },
    { ageDays: 365, l: 1, m: 75.7391, s: 0.03137 },
    { ageDays: 396, l: 1, m: 76.9304, s: 0.03154 },
    { ageDays: 426, l: 1, m: 78.0451, s: 0.03174 },
    { ageDays: 457, l: 1, m: 79.1613, s: 0.03197 },
    { ageDays: 487, l: 1, m: 80.2113, s: 0.03222 },
    { ageDays: 517, l: 1, m: 81.234, s: 0.03249 },
    { ageDays: 548, l: 1, m: 82.2628, s: 0.03279 },
    { ageDays: 578, l: 1, m: 83.2318, s: 0.0331 },
    { ageDays: 609, l: 1, m: 84.2074, s: 0.03342 },
    { ageDays: 639, l: 1, m: 85.1291, s: 0.03375 },
    { ageDays: 670, l: 1, m: 86.0589, s: 0.0341 },
    { ageDays: 700, l: 1, m: 86.9392, s: 0.03445 },
    { ageDays: 730, l: 1, m: 87.8018, s: 0.03479 },
  ],
  female: [
    { ageDays: 0, l: 1, m: 49.1477, s: 0.0379 },
    { ageDays: 30, l: 1, m: 53.6326, s: 0.03641 },
    { ageDays: 61, l: 1, m: 57.0796, s: 0.03568 },
    { ageDays: 91, l: 1, m: 59.7773, s: 0.0352 },
    { ageDays: 122, l: 1, m: 62.1071, s: 0.03486 },
    { ageDays: 152, l: 1, m: 64.019, s: 0.03463 },
    { ageDays: 183, l: 1, m: 65.751, s: 0.03448 },
    { ageDays: 213, l: 1, m: 67.2842, s: 0.03441 },
    { ageDays: 244, l: 1, m: 68.7732, s: 0.0344 },
    { ageDays: 274, l: 1, m: 70.1463, s: 0.03444 },
    { ageDays: 304, l: 1, m: 71.4656, s: 0.03452 },
    { ageDays: 335, l: 1, m: 72.7788, s: 0.03464 },
    { ageDays: 365, l: 1, m: 74.0049, s: 0.03479 },
    { ageDays: 396, l: 1, m: 75.2297, s: 0.03496 },
    { ageDays: 426, l: 1, m: 76.377, s: 0.03514 },
    { ageDays: 457, l: 1, m: 77.5258, s: 0.03534 },
    { ageDays: 487, l: 1, m: 78.6055, s: 0.03555 },
    { ageDays: 517, l: 1, m: 79.6559, s: 0.03576 },
    { ageDays: 548, l: 1, m: 80.7121, s: 0.03598 },
    { ageDays: 578, l: 1, m: 81.708, s: 0.0362 },
    { ageDays: 609, l: 1, m: 82.7116, s: 0.03643 },
    { ageDays: 639, l: 1, m: 83.6595, s: 0.03665 },
    { ageDays: 670, l: 1, m: 84.6154, s: 0.03689 },
    { ageDays: 700, l: 1, m: 85.5184, s: 0.03711 },
    { ageDays: 730, l: 1, m: 86.4008, s: 0.03733 },
  ],
};

/** Head-circumference-for-age, birth through 730 days (24 months), monthly LMS values plus the exact 24-month boundary day. */
export const WHO_HEAD_CIRCUMFERENCE_CM_REFERENCE: Readonly<
  Record<'male' | 'female', readonly WhoReferenceRow[]>
> = {
  male: [
    { ageDays: 0, l: 1, m: 34.4618, s: 0.03686 },
    { ageDays: 30, l: 1, m: 37.2435, s: 0.03135 },
    { ageDays: 61, l: 1, m: 39.1349, s: 0.02997 },
    { ageDays: 91, l: 1, m: 40.5008, s: 0.02918 },
    { ageDays: 122, l: 1, m: 41.6401, s: 0.02868 },
    { ageDays: 152, l: 1, m: 42.5524, s: 0.02837 },
    { ageDays: 183, l: 1, m: 43.3393, s: 0.02817 },
    { ageDays: 213, l: 1, m: 43.9791, s: 0.02804 },
    { ageDays: 244, l: 1, m: 44.5384, s: 0.02796 },
    { ageDays: 274, l: 1, m: 45.0007, s: 0.02792 },
    { ageDays: 304, l: 1, m: 45.4004, s: 0.0279 },
    { ageDays: 335, l: 1, m: 45.7593, s: 0.02789 },
    { ageDays: 365, l: 1, m: 46.0637, s: 0.02789 },
    { ageDays: 396, l: 1, m: 46.3421, s: 0.02789 },
    { ageDays: 426, l: 1, m: 46.5834, s: 0.02791 },
    { ageDays: 457, l: 1, m: 46.809, s: 0.02792 },
    { ageDays: 487, l: 1, m: 47.0088, s: 0.02795 },
    { ageDays: 517, l: 1, m: 47.1936, s: 0.02797 },
    { ageDays: 548, l: 1, m: 47.3718, s: 0.028 },
    { ageDays: 578, l: 1, m: 47.5341, s: 0.02803 },
    { ageDays: 609, l: 1, m: 47.6931, s: 0.02806 },
    { ageDays: 639, l: 1, m: 47.8399, s: 0.0281 },
    { ageDays: 670, l: 1, m: 47.985, s: 0.02814 },
    { ageDays: 700, l: 1, m: 48.1198, s: 0.02817 },
    { ageDays: 730, l: 1, m: 48.2494, s: 0.02821 },
  ],
  female: [
    { ageDays: 0, l: 1, m: 33.8787, s: 0.03496 },
    { ageDays: 30, l: 1, m: 36.5163, s: 0.03211 },
    { ageDays: 61, l: 1, m: 38.258, s: 0.03168 },
    { ageDays: 91, l: 1, m: 39.521, s: 0.0314 },
    { ageDays: 122, l: 1, m: 40.5895, s: 0.03119 },
    { ageDays: 152, l: 1, m: 41.454, s: 0.03102 },
    { ageDays: 183, l: 1, m: 42.2079, s: 0.03087 },
    { ageDays: 213, l: 1, m: 42.8278, s: 0.03075 },
    { ageDays: 244, l: 1, m: 43.3753, s: 0.03063 },
    { ageDays: 274, l: 1, m: 43.8309, s: 0.03053 },
    { ageDays: 304, l: 1, m: 44.2273, s: 0.03044 },
    { ageDays: 335, l: 1, m: 44.5865, s: 0.03035 },
    { ageDays: 365, l: 1, m: 44.894, s: 0.03027 },
    { ageDays: 396, l: 1, m: 45.1779, s: 0.03019 },
    { ageDays: 426, l: 1, m: 45.4255, s: 0.03012 },
    { ageDays: 457, l: 1, m: 45.6582, s: 0.03005 },
    { ageDays: 487, l: 1, m: 45.865, s: 0.02999 },
    { ageDays: 517, l: 1, m: 46.0571, s: 0.02993 },
    { ageDays: 548, l: 1, m: 46.2431, s: 0.02987 },
    { ageDays: 578, l: 1, m: 46.4135, s: 0.02982 },
    { ageDays: 609, l: 1, m: 46.5814, s: 0.02977 },
    { ageDays: 639, l: 1, m: 46.7375, s: 0.02972 },
    { ageDays: 670, l: 1, m: 46.8931, s: 0.02967 },
    { ageDays: 700, l: 1, m: 47.0388, s: 0.02962 },
    { ageDays: 730, l: 1, m: 47.1799, s: 0.02958 },
  ],
};

/** BMI-for-age, birth through 730 days (24 months), recumbent-length-based (WHO's `loh = 'L'` rows, matching `WHO_LENGTH_CM_REFERENCE`'s own convention) -- monthly LMS values plus the exact 24-month boundary day. */
export const WHO_BMI_REFERENCE: Readonly<Record<'male' | 'female', readonly WhoReferenceRow[]>> = {
  male: [
    { ageDays: 0, l: -0.3053, m: 13.4069, s: 0.0956 },
    { ageDays: 30, l: 0.2738, m: 14.914, s: 0.09035 },
    { ageDays: 61, l: 0.1113, m: 16.3231, s: 0.08676 },
    { ageDays: 91, l: 0.0077, m: 16.895, s: 0.08496 },
    { ageDays: 122, l: -0.0732, m: 17.1594, s: 0.08378 },
    { ageDays: 152, l: -0.1366, m: 17.2914, s: 0.08297 },
    { ageDays: 183, l: -0.1919, m: 17.3424, s: 0.08233 },
    { ageDays: 213, l: -0.2384, m: 17.3289, s: 0.08183 },
    { ageDays: 244, l: -0.2808, m: 17.2633, s: 0.08139 },
    { ageDays: 274, l: -0.3177, m: 17.1659, s: 0.08102 },
    { ageDays: 304, l: -0.3512, m: 17.0503, s: 0.08068 },
    { ageDays: 335, l: -0.383, m: 16.9231, s: 0.08037 },
    { ageDays: 365, l: -0.4113, m: 16.7992, s: 0.08009 },
    { ageDays: 396, l: -0.4384, m: 16.6731, s: 0.07982 },
    { ageDays: 426, l: -0.4629, m: 16.5553, s: 0.07958 },
    { ageDays: 457, l: -0.4867, m: 16.4393, s: 0.07934 },
    { ageDays: 487, l: -0.5082, m: 16.3335, s: 0.07913 },
    { ageDays: 517, l: -0.5286, m: 16.2343, s: 0.07893 },
    { ageDays: 548, l: -0.5485, m: 16.1388, s: 0.07873 },
    { ageDays: 578, l: -0.5667, m: 16.0536, s: 0.07854 },
    { ageDays: 609, l: -0.5847, m: 15.9737, s: 0.07836 },
    { ageDays: 639, l: -0.6013, m: 15.9043, s: 0.07818 },
    { ageDays: 670, l: -0.6176, m: 15.8405, s: 0.07802 },
    { ageDays: 700, l: -0.6328, m: 15.7853, s: 0.07786 },
    { ageDays: 730, l: -0.6473, m: 15.7356, s: 0.07771 },
  ],
  female: [
    { ageDays: 0, l: -0.0631, m: 13.3363, s: 0.09272 },
    { ageDays: 30, l: 0.3481, m: 14.5422, s: 0.09559 },
    { ageDays: 61, l: 0.1743, m: 15.7713, s: 0.09371 },
    { ageDays: 91, l: 0.0652, m: 16.3531, s: 0.09255 },
    { ageDays: 122, l: -0.0197, m: 16.6722, s: 0.09166 },
    { ageDays: 152, l: -0.086, m: 16.8379, s: 0.09096 },
    { ageDays: 183, l: -0.1436, m: 16.9086, s: 0.09035 },
    { ageDays: 213, l: -0.1915, m: 16.9021, s: 0.08984 },
    { ageDays: 244, l: -0.2351, m: 16.839, s: 0.08938 },
    { ageDays: 274, l: -0.2726, m: 16.7404, s: 0.08898 },
    { ageDays: 304, l: -0.3064, m: 16.62, s: 0.08862 },
    { ageDays: 335, l: -0.3382, m: 16.4867, s: 0.08827 },
    { ageDays: 365, l: -0.3665, m: 16.3578, s: 0.08797 },
    { ageDays: 396, l: -0.3934, m: 16.2298, s: 0.08768 },
    { ageDays: 426, l: -0.4176, m: 16.1132, s: 0.08741 },
    { ageDays: 457, l: -0.441, m: 16.0013, s: 0.08716 },
    { ageDays: 487, l: -0.4623, m: 15.9017, s: 0.08693 },
    { ageDays: 517, l: -0.4823, m: 15.8108, s: 0.08671 },
    { ageDays: 548, l: -0.5018, m: 15.726, s: 0.0865 },
    { ageDays: 578, l: -0.5197, m: 15.6524, s: 0.08631 },
    { ageDays: 609, l: -0.5374, m: 15.585, s: 0.08611 },
    { ageDays: 639, l: -0.5536, m: 15.5281, s: 0.08594 },
    { ageDays: 670, l: -0.5697, m: 15.4782, s: 0.08576 },
    { ageDays: 700, l: -0.5846, m: 15.4381, s: 0.0856 },
    { ageDays: 730, l: -0.5989, m: 15.4052, s: 0.08545 },
  ],
};
